import { ContextualInfoButtonWithText } from "@/components/buttons/ContextualInfoButton";
import { DownloadButton } from "@/components/buttons/DownloadButton";
import { DescriptionList } from "@/components/display/DescriptionList";
import { Loading } from "@/components/overlays/Loading";
import { SignificantHazards } from "@/components/status/SignificantHazards";
import { CustomTabs } from "@/components/tabs/CustomTabs";
import { getConfig, getRiskBandsMode, isTpuLabellingEnabled } from "@/features/branding";
import { GEOCODE_QUALITY_PROVIDED, GeocodingHelpButton } from "@/features/geocoders";
import { AnalysisResultsTab } from "@/features/maps";
import { ExposuresByHazard } from "@/features/pe_aldd";
import { getRiskBands, RiskBandsList } from "@/features/risk_bands";
import { logToolUsage } from "@/features/tracking";
import { LatLng } from "@/lib/crg_apis/types/latlng";
import { RiskBandsRequest, RiskBandsResponse, RiskBandsResponseSingleRcp } from "@/store/services/facades/types";
import { useLazyGetTpuLabellingQuery } from "@/store/services/supabase";
import { GeocodeWithResult } from "@/tools/risk-bands/components/MultipleResultsTable";
import ScreeningHazardExposures from "@/tools/risk-bands/components/ScreeningHazardExposures";
import { RiskBandsAdvancedSettingsValues } from "@/tools/risk-bands/types";
import { downloadRiskBandsCsv } from "@/tools/risk-bands/utils/download";
import { downloadExposureCsv } from "@/tools/risk-bands/utils/downloadExposure";
import { screeningFloodExposureReturnPeriod } from "@/tools/risk-bands/utils/shared";
import { Rcp, rcpValues } from "@/types/rcp";
import { concatLatLng } from "@/utils/geocoding";
import { formatSettingSwitchValue } from "@/utils/string";
import ArrowLeftOutlined from "@ant-design/icons/es/icons/ArrowLeftOutlined";
import CloseSquareFilled from "@ant-design/icons/es/icons/CloseSquareFilled";
import CompressOutlined from "@ant-design/icons/es/icons/CompressOutlined";
import { Button, Col, Modal, notification, Row, Select, Tag, Typography, Watermark } from "antd";
import { TabsProps } from "antd/lib";
import { MutableRefObject, useEffect, useMemo, useRef, useState } from "react";
import { useTranslation } from "react-i18next";
import styles from "./SingleResultModal.module.scss";
const {
  Title
} = Typography;
export type PointModalTabs = "risk" | "significantHazards" | "exposure";
interface SingleResultModalProps {
  address: string;
  advancedSettings: RiskBandsAdvancedSettingsValues;
  onClose: () => void;
  onReset: () => void;
  position: LatLng;
  customResponse?: RiskBandsResponse;
  setRefs?: (refs: SingleResultModalRefs) => void;
  tutorialSelectedTab: PointModalTabs | undefined;
}
export interface SingleResultModalRefs {
  back: MutableRefObject<null>;
  downloadCsv: MutableRefObject<null>;
  newSingleAsset: MutableRefObject<null>;
  rcp: MutableRefObject<null>;
  analysisInput: MutableRefObject<null>;
  analysisTabs: MutableRefObject<null>;
}
type ScenarioOptsType = {
  value: Rcp;
  label: string;
}[];

/**
 * Modal which displays the risk bands of a single asset.
 */
export const SingleResultModal = (props: SingleResultModalProps) => {
  const {
    defaultMetadata
  } = getConfig();
  const [ratings, setRatings] = useState<RiskBandsResponseSingleRcp>();
  const [response, setResponse] = useState<RiskBandsResponse>();
  const [scenario, setScenario] = useState<Rcp>("rcp85");
  const tpuLabellingEnabled = isTpuLabellingEnabled();
  const {
    t
  } = useTranslation("terms");
  const {
    t: tools
  } = useTranslation("tools", {
    keyPrefix: "settings"
  });
  const [getTpuLabelling, {
    isSuccess: isTpuSuccess,
    isError: isTpuError,
    data: tpuData
  }] = useLazyGetTpuLabellingQuery();
  const id = concatLatLng(props.position);
  const backRef = useRef(null);
  const downloadCsvRef = useRef(null);
  const newSingleAssetRef = useRef(null);
  const rcpRef = useRef(null);
  const analysisInputRef = useRef(null);
  const analysisTabsRef = useRef(null);
  const scenarioOpts: ScenarioOptsType = rcpValues.map(rcp => {
    return {
      value: rcp,
      label: t(`scenarios.${rcp}.medium`)
    };
  });
  useEffect(() => {
    if (props?.tutorialSelectedTab) {
      setActiveTab(props.tutorialSelectedTab);
    }
  }, [props?.tutorialSelectedTab]);
  useEffect(() => {
    if (!props.setRefs) {
      return;
    }
    props.setRefs({
      back: backRef,
      downloadCsv: downloadCsvRef,
      newSingleAsset: newSingleAssetRef,
      rcp: rcpRef,
      analysisInput: analysisInputRef,
      analysisTabs: analysisTabsRef
    });
  }, [backRef, downloadCsvRef, rcpRef]);
  useEffect(() => {
    if (props.customResponse) {
      setResponse(props.customResponse);
      setRatings(props.customResponse["rcp85"]);
      return;
    }
    if (tpuLabellingEnabled) {
      getTpuLabelling({
        [id]: [props.position.lat, props.position.lng]
      });
    }
    const requestData = {
      coordinates: {
        latitude: props.position.lat,
        longitude: props.position.lng
      },
      archetype: props.advancedSettings?.archetype,
      build_year: props.advancedSettings?.buildYear.amount,
      thresholds: {
        flood: {
          height_above_ground: props.advancedSettings?.heightAboveGround.amount || 0
        }
      },
      ...{
        anomaly_management_rp: props.advancedSettings?.anomalyManagementRp ? defaultMetadata.anomalyManagementRp : defaultMetadata.anomalyManagementRpDisabled
      },
      ...{
        defences: props.advancedSettings?.defences ? defaultMetadata.defences : defaultMetadata.defencesDisabled
      }
    } as RiskBandsRequest;
    const promise = getRiskBands(requestData);
    promise.then(({
      data,
      status
    }) => {
      if (status === "rejected") {
        notification.open({
          message: "Unable to load data. If this error persists, please contact the administrator.",
          type: "error"
        });
        props.onClose();
        return;
      }
      if (!ratings) {
        logToolUsage({
          id: "screening.single",
          type: "single_asset:simple_risk_bands",
          archetype: props.advancedSettings.archetype,
          count: 1
        });
      }
      setResponse(data);
      if (data) setRatings(data["rcp85"]);
    });
  }, []);
  useEffect(() => {
    if (!response) return;
    if (response.mode !== "abc") setRatings(response[scenario]);
  }, [scenario, response]);
  const downloadCsv = (mode: AnalysisResultsTab = "risk") => {
    if (!response) {
      return;
    }
    const results: GeocodeWithResult[] = [{
      result: response,
      geocode: {
        name: props.address,
        confidence: 100,
        input: props.address,
        source: "Search",
        latLng: props.position,
        quality: GEOCODE_QUALITY_PROVIDED
      }
    }];
    if (mode === "exposure") {
      downloadExposureCsv({
        advancedSettings: props.advancedSettings,
        filename: "single-asset - flood-exposure.csv",
        results,
        tpuData
      });
    } else {
      downloadRiskBandsCsv({
        advancedSettings: props.advancedSettings,
        filename: `single-asset - ${activeTab === "significantHazards" ? "significant-hazards" : "risk-bands"}.csv`,
        results,
        tpuData
      });
    }
  };
  const handleScenarioChange = (value: Rcp) => setScenario(value);
  const ShowTpuSubunit = () => {
    if (!tpuLabellingEnabled) return <></>;
    if (tpuLabellingEnabled && ratings && response && isTpuSuccess) {
      return <Col span={24} className="mb-2 text-lg">
          <Tag variant="filled" className="bg-subtle">
            <span className="text-gray-500">TPU:</span> {tpuData?.[id]?.tpu || "No Data"}
          </Tag>
          <Tag variant="filled" className="bg-subtle">
            <span className="text-gray-500">Subunit:</span> {tpuData?.[id]?.subunit || "No Data"}
          </Tag>

          {isTpuError && <Tag variant="filled" className="bg-subtle">
              <span className="text-gray-500">
                <CloseSquareFilled className="text-gray-400" /> Error fetching TPU and Subunit. Please try again.
              </span>
            </Tag>}
        </Col>;
    }
    return <></>;
  };
  const [activeTab, setActiveTab] = useState<PointModalTabs>("risk");
  const tabItems: TabsProps["items"] = useMemo(() => {
    const TabLabel = ({
      tabKey,
      label,
      hidden = false
    }: {
      tabKey: string;
      label: string;
      hidden?: boolean;
    }) => <div title={tabKey} className={hidden ? "hidden" : ""} style={{
      borderColor: activeTab === tabKey ? "var(--color-accent)" : "inherit"
    }} data-sentry-component="TabLabel" data-sentry-source-file="SingleResultModal.tsx">
        {label}
      </div>;
    return [{
      key: "risk",
      label: <TabLabel tabKey="risk" label={t("simpleRiskRatings")} />
    }, {
      key: "significantHazards",
      label: <TabLabel tabKey="significantHazards" label="Significant Hazards" />
    }, {
      key: "exposure",
      label: <TabLabel tabKey="exposure" label="Flood Exposure" />
    }];
  }, [activeTab]);
  return <Modal destroyOnHidden onCancel={props.onClose} footer={null} open={true} width={getRiskBandsMode() === "abc" ? undefined : 860} closeIcon={false} data-sentry-element="Modal" data-sentry-component="SingleResultModal" data-sentry-source-file="SingleResultModal.tsx">
      <div className="flex items-center justify-between pb-4">
        <Button ref={backRef} type="text" icon={<ArrowLeftOutlined />} onClick={props.onClose} data-sentry-element="Button" data-sentry-source-file="SingleResultModal.tsx">
          Back
        </Button>
        <Button ref={newSingleAssetRef} type="text" icon={<CompressOutlined />} onClick={props.onReset} data-sentry-element="Button" data-sentry-source-file="SingleResultModal.tsx">
          New Single Asset
        </Button>
      </div>

      <div className="rounded-md border border-subtle p-4 pb-8">
        <Row className="text-center" data-sentry-element="Row" data-sentry-source-file="SingleResultModal.tsx">
          <Col span={24} data-sentry-element="Col" data-sentry-source-file="SingleResultModal.tsx">
            <Title className="!mb-1 mt-0" data-sentry-element="Title" data-sentry-source-file="SingleResultModal.tsx">{props.address}</Title>
          </Col>
          <ShowTpuSubunit data-sentry-element="ShowTpuSubunit" data-sentry-source-file="SingleResultModal.tsx" />
        </Row>
        <Row ref={analysisInputRef} align={"top"} className="mt-4" gutter={[12, 0]} data-sentry-element="Row" data-sentry-source-file="SingleResultModal.tsx">
          <Col span={13} data-sentry-element="Col" data-sentry-source-file="SingleResultModal.tsx">
            <DescriptionList className="mt-4" labelWidth={175} items={[{
            label: "Total Number of Points",
            value: 1
          }, {
            label: t("location"),
            value: props.address
          }, {
            label: <div className="flex items-center">
                      <span>Geocode Quality</span>
                      <GeocodingHelpButton className="*:!text-gray-400" />
                    </div>,
            value: GEOCODE_QUALITY_PROVIDED
          }, {
            label: <ContextualInfoButtonWithText id="archetypesExplained" drawerWidth={750}>
                      Archetype
                    </ContextualInfoButtonWithText>,
            value: props.advancedSettings?.archetype
          }]} data-sentry-element="DescriptionList" data-sentry-source-file="SingleResultModal.tsx" />
          </Col>
          <Col span={11} data-sentry-element="Col" data-sentry-source-file="SingleResultModal.tsx">
            <DescriptionList className="mt-4" labelWidth={220} items={[{
            label: <ContextualInfoButtonWithText id="buildYear">{t("buildYear")}</ContextualInfoButtonWithText>,
            value: props.advancedSettings?.buildYear.amount
          }, {
            label: <ContextualInfoButtonWithText id="heightAboveGround">
                      {t("heightAboveGround")}
                    </ContextualInfoButtonWithText>,
            value: props.advancedSettings?.heightAboveGround.amount
          }, {
            label: <ContextualInfoButtonWithText id="anomalyManagementRp">
                      {tools("anomalyManagementRp.title")}
                    </ContextualInfoButtonWithText>,
            value: formatSettingSwitchValue(props.advancedSettings?.anomalyManagementRp)
          }, {
            label: <ContextualInfoButtonWithText id="defences">{tools("defences.title")}</ContextualInfoButtonWithText>,
            value: formatSettingSwitchValue(props.advancedSettings?.defences)
          }]} data-sentry-element="DescriptionList" data-sentry-source-file="SingleResultModal.tsx" />
          </Col>
        </Row>
      </div>

      <div ref={analysisTabsRef} className="mt-6">
        <CustomTabs items={tabItems} activeKey={activeTab} onChange={key => setActiveTab(key as PointModalTabs)} compact data-sentry-element="CustomTabs" data-sentry-source-file="SingleResultModal.tsx" />
      </div>
      <div className="relative z-[0] !-mt-[1px] rounded-md border border-subtle p-4 pb-8">
        {ratings && response ? <div className={styles.fadeIn} key={scenario}>
            {(activeTab === "risk" || activeTab === "significantHazards") && <div className="mb-2 mt-4 flex w-full items-center justify-between">
                {activeTab === "risk" ? <ContextualInfoButtonWithText rootClassName="text-base font-semibold " id="simpleRiskRatings">
                    {t("simpleRiskRatings")}
                  </ContextualInfoButtonWithText> : <ContextualInfoButtonWithText rootClassName="text-base font-semibold " id="significantHazards">
                    Significant Hazards
                  </ContextualInfoButtonWithText>}
                <div className="flex items-center gap-1" ref={rcpRef}>
                  <ContextualInfoButtonWithText rootClassName="font-semibold" id="emissionScenarios">
                    Scenarios
                  </ContextualInfoButtonWithText>
                  <Select className="min-w-[355px]" onChange={handleScenarioChange} options={scenarioOpts} defaultValue={scenario} />
                </div>
              </div>}
            {activeTab === "risk" && <>
                <div className="mt-8 flex gap-2">
                  {!props.customResponse && <RiskBandsList ratings={ratings} />}
                  {props.customResponse && <Watermark gap={[48, 32]} content={["EXAMPLE DATA", "For Demonstration Only"]} font={{
              color: "#ff0000"
            }}>
                      <RiskBandsList ratings={ratings} />
                    </Watermark>}
                </div>
                <div ref={downloadCsvRef} className="mt-8 flex justify-end">
                  <DownloadButton type="primary" onClick={() => downloadCsv()}>
                    Download {t("simpleRiskRatings")}
                  </DownloadButton>
                </div>
              </>}
            {activeTab === "significantHazards" && <div className="mt-4">
                <SignificantHazards noTitle significantHz={"top_significant_hazards" in ratings ? ratings.top_significant_hazards : []} incorporatedHz={response.metadata.incorporated_hazards} />
                <div className="mt-4 flex justify-end">
                  <DownloadButton type="primary" onClick={() => downloadCsv()}>
                    Download Significant Hazards
                  </DownloadButton>
                </div>
              </div>}
            {activeTab === "exposure" && <div className="mt-5">
                <ScreeningHazardExposures limitReturnPeriods={[screeningFloodExposureReturnPeriod]} exposure={"exposures" in response.rcp85 ? response.rcp85.exposures as ExposuresByHazard : undefined} displayReturnPeriod={screeningFloodExposureReturnPeriod} />

                <div className="mt-6 flex justify-end">
                  <DownloadButton type="primary" onClick={() => downloadCsv("exposure")}>
                    Download Flood Exposure
                  </DownloadButton>
                </div>
              </div>}
          </div> : <div className="flex h-48 w-full items-center justify-center">
            <Loading />
          </div>}
      </div>
    </Modal>;
};